import { Toggle as BaseToggle } from "@base-ui/react/toggle";
import { ToggleGroup as BaseToggleGroup } from "@base-ui/react/toggle-group";
import { useState, type ReactNode } from "react";
import cx from "ui/cx";
import type { WithTooltipProps } from "ui/popovers/Tooltip";
import { wrapWithTooltip } from "ui/popovers/Tooltip";
import styles from "./ToggleGroup.module.scss";

export type ToggleGroupStyle = "primary" | "secondary" | "tertiary";

interface ToggleGroupCommonProps
  extends Omit<React.ComponentPropsWithoutRef<"div">, "onChange" | "defaultValue" | "dir"> {
  allowEmpty?: boolean;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  orientation?: "horizontal" | "vertical";
  toggleStyle?: ToggleGroupStyle;
}

export interface ToggleGroupSingleProps extends ToggleGroupCommonProps {
  type: "single";
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

export interface ToggleGroupMultipleProps extends ToggleGroupCommonProps {
  type: "multiple";
  value?: string[];
  defaultValue?: string[];
  onValueChange?: (value: string[]) => void;
}

export type ToggleGroupProps = ToggleGroupSingleProps | ToggleGroupMultipleProps;

export interface ToggleGroupItemProps
  extends Omit<React.ComponentProps<typeof BaseToggle>, "render" | "children" | "value">,
    WithTooltipProps {
  value: string;
  children: ReactNode;
  /** Class for the toggle `<button>`, which carries `[data-pressed]` and the item chrome. */
  className?: string;
  /**
   * Class for the inner content box, which owns the item's padding and min-height. Size and spacing
   * overrides belong here: `.item` has `padding: 0` and shrink-wraps this box, so a height set on
   * the button alone is overflowed by this box's own padding.
   */
  contentClassName?: string;
}

// Public API value is a string (single) or string[] (multiple); Base UI always wants string[].
function toArrayValue(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) {
    return value;
  }
  return value ? [value] : [];
}

function ToggleGroupRoot(props: ToggleGroupProps) {
  const {
    children,
    toggleStyle = "primary",
    disabled = false,
    className,
    orientation = "horizontal",
    allowEmpty = true,
    onValueChange,
    value: propValue,
    defaultValue,
    type,
    ...restProps
  } = props;

  const [internalValue, setInternalValue] = useState<string[]>(() => toArrayValue(propValue ?? defaultValue));

  const isControlled = propValue !== undefined;
  const currentValue = isControlled ? toArrayValue(propValue) : internalValue;

  const handleValueChange = (groupValue: string[]) => {
    if (disabled) {
      return;
    }

    if (type === "single") {
      const nextValue = groupValue[groupValue.length - 1] ?? "";
      if (nextValue === "" && !allowEmpty) {
        return;
      }
      if (!isControlled) {
        setInternalValue(nextValue ? [nextValue] : []);
      }
      (onValueChange as ((value: string) => void) | undefined)?.(nextValue);
      return;
    }

    if (groupValue.length === 0 && !allowEmpty) {
      return;
    }
    if (!isControlled) {
      setInternalValue(groupValue);
    }
    (onValueChange as ((value: string[]) => void) | undefined)?.(groupValue);
  };

  return (
    <BaseToggleGroup
      value={currentValue}
      multiple={type === "multiple"}
      disabled={disabled}
      orientation={orientation}
      onValueChange={handleValueChange}
      className={cx(
        styles.root,
        orientation === "vertical" && styles.vertical,
        disabled && styles.disabled,
        styles[toggleStyle],
        className,
      )}
      data-testid="toggle-group"
      {...restProps}
    >
      {children}
    </BaseToggleGroup>
  );
}

function ToggleGroupItem({
  children,
  disabled = false,
  className,
  contentClassName,
  tooltip,
  value,
  ...props
}: ToggleGroupItemProps) {
  return (
    <BaseToggle
      value={value}
      disabled={disabled}
      className={cx(styles.item, className)}
      data-testid="toggle-group-item"
      {...props}
    >
      {/* Tooltip requires an element to attach props to, so wrap children in a div. */}
      {wrapWithTooltip(<div className={cx(styles.itemWrapper, contentClassName)}>{children}</div>, tooltip)}
    </BaseToggle>
  );
}

// Compound component exports
export const ToggleGroup = {
  Root: ToggleGroupRoot,
  Item: ToggleGroupItem,
};

// Individual component exports for flexibility
export { ToggleGroupRoot, ToggleGroupItem };

export default ToggleGroup;
